/**
 * Keeping the draft across reloads — and getting rid of it in time.
 *
 * **The draft holds personal data**: a street address, and a comment that routinely says how to
 * reach the car («код 4512», «во дворе за шлагбаумом, ключи у консьержа»). A browser has no
 * equivalent of Android's `EncryptedSharedPreferences`, so the protection here is not encryption
 * but lifetime: the draft expires 24 hours after it was last touched, and it is wiped outright on
 * sign-out. On a shared computer that is the difference that matters.
 *
 * `localStorage`, not `sessionStorage`: surviving a closed tab is the entire point — that is the
 * case docs/20 §3 is about. `sessionStorage` would lose the draft in the one scenario it exists
 * for.
 *
 * Every read is defensive. A draft written by an older build, hand-edited, or truncated by a
 * storage quota must resolve to «no draft» and never to a half-populated one: a wizard resumed at
 * the payment step with no car is worse than a wizard started again.
 */
import type { OrderDraft } from './model/draft';
import { ORDER_STEPS } from './model/stateMachine';

const KEY = 'tt:order-draft';

/** How long a draft lives after the last edit. */
const TTL_MS = 24 * 60 * 60 * 1000;

interface StoredDraft {
  savedAt: number;
  draft: OrderDraft;
}

/**
 * The fields without which a resumed draft is nonsense.
 *
 * Deliberately a shape check rather than a schema library: this runs on every start, and the only
 * question worth asking is «can the wizard open on this».
 */
function looksLikeDraft(value: unknown): value is OrderDraft {
  if (value === null || typeof value !== 'object') return false;
  const draft = value as Partial<OrderDraft>;
  return (
    typeof draft.externalId === 'string' &&
    draft.externalId.length > 0 &&
    typeof draft.step === 'string' &&
    ORDER_STEPS.includes(draft.step) &&
    Array.isArray(draft.extraCars) &&
    typeof draft.comment === 'string' &&
    typeof draft.liters === 'string'
  );
}

/**
 * Brings a draft written by an older build up to what this one expects.
 *
 * A draft outlives a deploy — that is the entire point of storing it — so every field this app
 * tightens has to be reconciled here. `presence` used to be nullable and defaulted to `null`;
 * loading such a draft into a build that assumes an answer leaves «Далее» dead on a step that
 * looks fully filled in, which is exactly the defect making it non-nullable was meant to fix.
 *
 * Deliberately a repair, not a rejection: throwing the draft away would cost somebody the four
 * steps they had already filled in, over a field with an obvious default.
 */
function migrate(draft: OrderDraft): OrderDraft {
  // Часы работы появились на черновике 01.10.2026; у черновика старой сборки поля нет вовсе, а
  // `undefined` там, где ждут `null`, — та же ловушка, что была с `presence`.
  const withHours = draft.serviceHours === undefined ? { ...draft, serviceHours: null } : draft;
  if (withHours.presence === 'withMe' || withHours.presence === 'withoutMe') return withHours;
  return { ...withHours, presence: 'withMe' };
}

export function saveOrderDraft(draft: OrderDraft, now: number = Date.now()): void {
  try {
    const payload: StoredDraft = { savedAt: now, draft };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded, or storage disabled in a locked-down browser. The wizard keeps working from
    // memory for this session; only the survive-a-reload promise is lost, and there is nothing
    // useful to say about it mid-order.
  }
}

/**
 * The stored draft, or `null` when there is none, it expired, or it cannot be trusted.
 *
 * An expired or malformed entry is removed as it is read, so a bad value cannot sit there being
 * re-parsed and re-rejected on every start.
 */
export function loadOrderDraft(now: number = Date.now()): OrderDraft | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      clearOrderDraft();
      return null;
    }
    const { savedAt, draft } = parsed as Partial<StoredDraft>;
    if (typeof savedAt !== 'number' || now - savedAt > TTL_MS) {
      clearOrderDraft();
      return null;
    }
    if (!looksLikeDraft(draft)) {
      clearOrderDraft();
      return null;
    }
    return migrate(draft);
  } catch {
    clearOrderDraft();
    return null;
  }
}

export function clearOrderDraft(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do and nothing to tell anyone — the TTL is the backstop.
  }
}
